/**
 * Replays.
 *
 * A run is fully described by (rules version, mode, seed, list of turns). The
 * client records every accepted turn as `[tick, direction]`; the leaderboard
 * worker re-runs the simulation from that and only trusts the score if its own
 * result matches. A forged score has to be a forged *replay* that actually
 * plays out — which is just... playing the game.
 *
 * The wire format is a varint stream of `(deltaTick << 2) | dir`, base64'd.
 * A twelve-minute run is a couple of kilobytes.
 */

import { createSim, queueTurn, step, summarize } from './sim'
import { rulesFor, RULES_VERSION } from './rules'
import type { Dir, ModeId, RunSummary } from './types'

export interface Turn {
  tick: number
  dir: Dir
}

export interface ReplayPayload {
  v: number
  mode: ModeId
  seed: number
  turns: string
}

/**
 * Runaway guard for untrusted input. 40k ticks is ~45 minutes of play at the
 * fastest coil, and re-simulating that costs roughly 12ms — inside the 10ms
 * CPU budget of a free Cloudflare Worker for any realistic run (a 9k-tick run
 * verifies in under 3ms) with headroom for the pathological case.
 */
const MAX_TICKS = 40_000
const MAX_TURNS = 20_000

export function encodeTurns(turns: readonly Turn[]): string {
  const bytes: number[] = []
  let prev = 0
  for (const t of turns) {
    // Deltas are unsigned, so an out-of-order log is simply unrepresentable.
    // Throwing here turns a would-be silent corruption into a loud client bug.
    if (t.tick < prev) throw new Error('turns must be recorded in tick order')
    const delta = t.tick - prev
    prev = t.tick
    let value = delta * 4 + (t.dir & 3)
    while (value >= 0x80) {
      bytes.push((value & 0x7f) | 0x80)
      value = Math.floor(value / 128)
    }
    bytes.push(value)
  }
  return bytesToBase64(Uint8Array.from(bytes))
}

export function decodeTurns(encoded: string): Turn[] {
  const bytes = base64ToBytes(encoded)
  const turns: Turn[] = []
  let tick = 0
  let value = 0
  let shift = 1
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i]
    value += (b & 0x7f) * shift
    if (b & 0x80) {
      shift *= 128
      continue
    }
    tick += Math.floor(value / 4)
    turns.push({ tick, dir: (value & 3) as Dir })
    if (turns.length > MAX_TURNS) throw new Error('replay too long')
    value = 0
    shift = 1
  }
  return turns
}

/**
 * Re-run a replay to completion and report what actually happened. Shared by
 * the client (so a run is verified before it is even offered for submission)
 * and the worker (so nothing else has to be believed).
 */
export function verifyReplay(payload: ReplayPayload): RunSummary | null {
  if (payload.v !== RULES_VERSION) return null
  const rules = rulesFor(payload.mode)
  if (!rules || rules.id !== payload.mode) return null
  if (!Number.isInteger(payload.seed) || payload.seed < 0 || payload.seed > 0xffffffff) return null

  let turns: Turn[]
  try {
    turns = decodeTurns(payload.turns)
  } catch {
    return null
  }

  const sim = createSim(rules, payload.seed)
  let next = 0
  while (sim.alive && sim.tick < MAX_TICKS) {
    // Decoded ticks are monotonic by construction (unsigned deltas), so this
    // can only ever consume turns at or ahead of the current tick.
    while (next < turns.length && turns[next].tick === sim.tick) {
      queueTurn(sim, turns[next].dir)
      next++
    }
    step(sim, rules)
  }
  if (sim.alive) return null // ran out of budget without dying
  if (next < turns.length) return null // trailing turns that never applied
  return summarize(sim)
}

// ── base64 ───────────────────────────────────────────────────────────────
// btoa/atob exist in browsers and in Cloudflare Workers; this stays free of
// any platform-specific Buffer.

function bytesToBase64(bytes: Uint8Array): string {
  let bin = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(bin)
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}
